import React from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Icon,
  Paper,
  alpha,
  useTheme,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../hooks/useCart";
import { usePOSService } from "../../hooks/usePOSService";
import { PaymentForm } from "../../components/PaymentForm/PaymentForm";
import { ReceiptDialog } from "../../components/ReceiptDialog/ReceiptDialog";
import { OrderResponse } from "../../types/pos.types";

export const CheckoutConfirmationPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { items, clearCart, total, subtotal, tax } = useCart();

  const { posService: service, sessionId, isSessionActive } = usePOSService();

  const [isLoading, setIsLoading] = React.useState(false);
  const [receiptData, setReceiptData] = React.useState<any>(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = React.useState(false);

  const handlePaymentSubmit = async (paymentData: any) => {
    try {
      setIsLoading(true);
      // Create new order
      const response = await service.createOrder({
        items: items.map((item) => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.price,
        })),
        payment: {
          method: paymentData.method,
          amount: total,
          reference: paymentData.transactionId || "",
        },
      });

      if (response.success) {
        setReceiptData(response.data);
        setReceiptDialogOpen(true);
      }
    } catch (error) {
      console.error("Error processing payment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintReceipt = () => {
    // TODO: Implement printing functionality
    setReceiptDialogOpen(false);
    clearCart();
    navigate("/pos/orders");
  };

  const handleEmailReceipt = (email: string) => {
    if (!receiptData) return;
    try {
      setIsLoading(true);
      service
        .emailReceipt({
          id: receiptData.id,
          email: email,
        })
        .then((response) => {
          if (response.success) {
            setReceiptDialogOpen(false);
            clearCart();
            navigate("/pos/orders");
          }
        });
    } catch (error) {
      console.error("Error emailing receipt:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/pos/menu");
  };

  if (!isSessionActive) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          bgcolor: "background.default",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            maxWidth: 400,
            width: "100%",
            textAlign: "center",
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: 4,
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
          }}
        >
          <Icon sx={{ fontSize: 48, color: "text.secondary" }}>
            point_of_sale
          </Icon>
          <Typography variant="h6" gutterBottom>
            No Active Session
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You need an active session to process payments
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Icon>arrow_back</Icon>}
            onClick={() => navigate("/pos/orders")}
            sx={{ mt: 1 }}
          >
            Back to Orders
          </Button>
        </Paper>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      {/* Custom AppBar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.primary.dark,
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <IconButton edge="start" color="inherit" onClick={handleBack}>
            <Icon>arrow_back</Icon>
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
            Checkout
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Order Summary */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(8px)",
              boxShadow: `0 4px 20px ${alpha(
                theme.palette.common.black,
                0.05
              )}`,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>
            <Box sx={{ mt: 2 }}>
              {items.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1,
                  }}
                >
                  <Typography>
                    {item.quantity}x {item.name}
                  </Typography>
                  <Typography>
                    BHD {(item.price * item.quantity).toFixed(2)}
                  </Typography>
                </Box>
              ))}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Subtotal</Typography>
                  <Typography>BHD {subtotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Tax</Typography>
                  <Typography>BHD {tax.toFixed(2)}</Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                    pt: 2,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6">BHD {total.toFixed(2)}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Payment Form */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(8px)",
              boxShadow: `0 4px 20px ${alpha(
                theme.palette.common.black,
                0.05
              )}`,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Payment Method
            </Typography>
            <PaymentForm
              open={true}
              onClose={() => {}}
              onSubmit={handlePaymentSubmit}
              cart={{ items, subtotal, tax, total }}
            />
          </Paper>
        </Box>
      </Box>

      {receiptData && (
        <ReceiptDialog
          open={receiptDialogOpen}
          onClose={() => setReceiptDialogOpen(false)}
          onPrint={handlePrintReceipt}
          onEmail={handleEmailReceipt}
          receipt={receiptData}
        />
      )}
    </Box>
  );
};
